<template>
  <!-- Up to three assets composed as one object: a main plate, something in
       front of it and something behind it, each on its own depth so scroll
       separates them. Which composition is used follows what the project
       actually has, never a per-project layout field. -->
  <div class="cluster" :class="`cluster--${shape}`" :style="{ '--n-slots': slots.length }">
    <div
      v-for="(s, i) in slots"
      :key="s.src"
      class="slot"
      :class="`slot--${s.role}`"
      :style="{ '--n': i }"
    >
      <div :ref="(el) => setDepth(el, s)" class="slot-depth">
        <StageFrame :src="s.src" :kind="s.frame" :alt="s.alt" :eager="eager && s.role === 'main'" />
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount } from "vue";
import StageFrame from "@/components/project/StageFrame.vue";
import { registerLayer, DEPTH } from "@/composables/useDepth";

/* Depth per role. Main is the plate; front sits nearest and travels least;
   back is furthest and travels most, which is what makes the three read as
   one object at different distances rather than three pictures. */
const K = { main: DEPTH.PLATE, front: DEPTH.NEAR, back: DEPTH.BACK };

export default {
  name: "StageCluster",
  components: { StageFrame },
  props: {
    /* [{ src, frame, alt }] in the project's own order, already measured. */
    assets: { type: Array, required: true },
    eager: { type: Boolean, default: false },
  },
  setup(props) {
    const stops = new Map();

    /* Cast the roles from the material. The first asset leads. If it is a
       window, a phone goes in front (or a second window if there is none)
       and a window behind. If it leads with a phone, it is an app: three
       phones fanned. */
    const cast = computed(() => {
      const list = props.assets.filter((a) => a && a.src);
      if (!list.length) return { shape: "none", slots: [] };
      const main = list[0];
      const rest = list.slice(1);
      // A pre-framed device mockup is a phone for casting purposes.
      const isPhone = (a) => a.frame === "phone" || a.frame === "device";
      const pick = (frame) => {
        const i = rest.findIndex((a) => (frame === "phone" ? isPhone(a) : a.frame === frame));
        return i === -1 ? null : rest.splice(i, 1)[0];
      };

      if (isPhone(main)) {
        const left = pick("phone");
        const right = pick("phone");
        const slots = [
          { ...main, role: "main" },
          left && { ...left, role: "back" },
          right && { ...right, role: "front" },
        ].filter(Boolean);
        return { shape: slots.length === 3 ? "fan" : slots.length === 2 ? "pair" : "single", slots };
      }

      const front = pick("phone") || pick("window") || pick("bare");
      const back = pick("window") || pick("bare") || pick("phone");
      const slots = [
        { ...main, role: "main" },
        front && { ...front, role: "front" },
        back && { ...back, role: "back" },
      ].filter(Boolean);
      const shape =
        !front ? "single" : isPhone(front) ? (back ? "desk-phone-desk" : "desk-phone") : back ? "desk-desk-desk" : "desk-desk";
      return { shape, slots };
    });

    const shape = computed(() => cast.value.shape);
    const slots = computed(() => cast.value.slots);

    const setDepth = (el, slot) => {
      if (!el) {
        const stop = stops.get(slot.src);
        if (stop) stop();
        stops.delete(slot.src);
        return;
      }
      if (stops.has(slot.src)) return;
      // Only the main plate is promoted. With all three pinned to their own
      // layer the page peaked at 9 against a ceiling of 8.
      stops.set(slot.src, registerLayer(el, { k: K[slot.role], promote: slot.role === "main" }));
    };

    onBeforeUnmount(() => stops.forEach((stop) => stop && stop()));

    return { shape, slots, setDepth };
  },
};
</script>

<style scoped>
.cluster {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 11;
}

/* Layout lives on the slot; the entrance lives on the slot; depth lives on
   the child. Three transforms on three boxes, so none of them fights. */
.slot {
  position: absolute;
  opacity: 0;
  transform: translate3d(var(--ex, 0px), var(--ey, 28px), 0);
  transition: opacity var(--d-5) var(--ease-out), transform var(--d-5) var(--ease-out);
  transition-delay: calc(var(--n) * 130ms);
}
.in .slot {
  opacity: 1;
  transform: none;
}
.slot--back {
  --ex: 24px;
  --ey: -24px;
  z-index: 1;
}
.slot--main {
  --ex: 0px;
  --ey: 32px;
  z-index: 2;
}
.slot--front {
  --ex: -20px;
  --ey: 36px;
  z-index: 3;
}

.slot-depth {
  --travel-y: calc(
    (var(--p, 0.5) - 0.5) * (1 - var(--k, 1)) * var(--travel) * var(--depth-scale)
  );
  transform: translate3d(var(--mx, 0px), var(--travel-y), 0);
  transition: transform var(--d-3) var(--ease-out);
}
/* The one hover on the page that moves something: the nearest plate lifts
   a little, which is what a real object on a table would do. */
.cluster:hover .slot--front .slot-depth {
  transform: translate3d(var(--mx, 0px), calc(var(--travel-y) - 6px), 0);
}

/* ── Window-led compositions ───────────────────────────────── */
.cluster--desk-phone-desk .slot--back,
.cluster--desk-desk-desk .slot--back,
.cluster--desk-desk .slot--back {
  top: 0;
  right: 0;
  width: 74%;
}
.cluster--desk-phone-desk .slot--main,
.cluster--desk-desk-desk .slot--main {
  left: 0;
  top: 15%;
  width: 84%;
}
.cluster--desk-phone .slot--main,
.cluster--desk-desk .slot--main,
.cluster--single .slot--main {
  left: 0;
  top: 4%;
  width: 90%;
}
.cluster--single .slot--main {
  width: 100%;
}
.cluster--desk-phone-desk .slot--front,
.cluster--desk-phone .slot--front {
  right: 1%;
  bottom: 0;
  height: 64%;
}
.cluster--desk-desk-desk .slot--front,
.cluster--desk-desk .slot--front {
  right: 0;
  bottom: 0;
  width: 56%;
}
/* Whatever is behind is a little further into the shade. */
.slot--back .slot-depth {
  opacity: 0.88;
}

/* A phone slot sizes its frame by height; the frame's own aspect ratio
   gives the width. */
.slot--front :deep(.sf--phone),
.cluster--fan .slot :deep(.sf--phone),
.cluster--pair .slot :deep(.sf--phone),
.slot--front :deep(.sf--device),
.cluster--fan .slot :deep(.sf--device),
.cluster--pair .slot :deep(.sf--device) {
  height: 100%;
  width: auto;
  max-width: none;
  aspect-ratio: 71.6 / 146.6;
}
.slot :deep(.sf--phone .sf-screen) {
  height: 100%;
}
/* A device mockup has its own proportions; let the picture set the width. */
.slot :deep(.sf--device) {
  aspect-ratio: auto;
}
.slot--front,
.cluster--fan .slot,
.cluster--pair .slot {
  display: flex;
  justify-content: center;
}
.slot .slot-depth {
  height: 100%;
  width: 100%;
}
.slot--front .slot-depth,
.cluster--fan .slot-depth,
.cluster--pair .slot-depth {
  width: auto;
}

/* ── Phone-led: three phones fanned, the app's own screens ──── */
.cluster--fan .slot--back {
  left: 6%;
  bottom: 3%;
  height: 80%;
}
.cluster--fan .slot--main {
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
}
.cluster--fan .slot--front {
  right: 6%;
  bottom: 7%;
  height: 84%;
}
.cluster--pair .slot--main {
  left: 8%;
  bottom: 0;
  height: 100%;
}
.cluster--pair .slot--front {
  right: 8%;
  bottom: 6%;
  height: 86%;
}

@media (max-width: 720px) {
  .cluster {
    aspect-ratio: 4 / 3.4;
  }
  .cluster--desk-phone-desk .slot--back,
  .cluster--desk-desk-desk .slot--back,
  .cluster--desk-desk .slot--back {
    display: none;
  }
  .cluster--desk-phone-desk .slot--main,
  .cluster--desk-desk-desk .slot--main {
    top: 0;
    width: 92%;
  }
  .cluster--desk-phone-desk .slot--front,
  .cluster--desk-phone .slot--front {
    height: 56%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .slot {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
